import { useEffect, useRef } from 'react'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

/** Пауза после последней правки, через которую статья сохраняется сама. */
export const AUTOSAVE_DELAY_MS = 1500

interface Args<T> {
  /** Статья не на проверке. Новая статья создаётся тем же путём — первой паузой. */
  enabled: boolean
  /** Сохранять ли несохранённое при уходе со страницы. У НОВОЙ статьи — нет: создание
   *  на размонтировании угнало бы автора обратно в редактор (переход на адрес статьи)
   *  или, если правка шла во время создания, завело бы вторую статью. */
  flushOnLeave?: boolean
  dirty: boolean
  saving: boolean
  form: T
  /** Тихое сохранение текущей формы. */
  run: () => void
}

/**
 * Автосохранение статьи: через паузу после последней правки и при уходе со
 * страницы, если правка ещё не ушла. Пока летит сохранение, следующее ждёт его
 * конца — запросы не наслаиваются.
 */
export const useArticleAutosave = <T>({
  enabled,
  dirty,
  saving,
  form,
  run,
  flushOnLeave = true,
}: Args<T>) => {
  const settled = useDebouncedValue(form, AUTOSAVE_DELAY_MS)
  // Снимок, который уже уходил в `run`: после отказа форма та же, и повтор
  // слал бы тот же запрос в цикле. Новая форма (правка, ребейз) — новая ссылка.
  const attempted = useRef<T | null>(null)
  const latest = useRef({ enabled, dirty, run, form, flushOnLeave })
  latest.current = { enabled, dirty, run, form, flushOnLeave }

  useEffect(() => {
    if (!enabled || !dirty || saving || settled !== form || form === attempted.current) return
    attempted.current = form
    run()
    // `run`/`form` — свежие на каждом рендере; триггер — осевшая форма и конец сохранения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, saving])

  useEffect(
    () => () => {
      const {
        enabled: on,
        dirty: pending,
        run: flush,
        form: last,
        flushOnLeave: leave,
      } = latest.current
      if (leave && on && pending && last !== attempted.current) flush()
    },
    [],
  )
}
